import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getDefaultQuestionnaire,
  getIndustryMultiples,
  calculateVAC,
  saveQuestionnaireResponses
} from '../../services/api';
import './VACWizard.css';

// Auto-save debounce delay in milliseconds
const AUTO_SAVE_DELAY = 1000;

// Local storage key prefix
const STORAGE_KEY_PREFIX = 'vac_draft_';

const VACWizard = ({ valuationId, userId, onComplete, userType = 'owner' }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [riskScore, setRiskScore] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [hasDraft, setHasDraft] = useState(false);

  const autoSaveTimerRef = useRef(null);
  const storageKey = `${STORAGE_KEY_PREFIX}${valuationId || 'new'}`;

  // Load draft from localStorage
  const loadDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const draft = JSON.parse(saved);
        return draft;
      }
    } catch (err) {
      console.error('Failed to load draft:', err);
    }
    return null;
  }, [storageKey]);

  // Save draft to localStorage
  const saveDraft = useCallback((data) => {
    try {
      const draft = {
        responses: data.responses,
        currentSectionIndex: data.currentSectionIndex,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(storageKey, JSON.stringify(draft));
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save draft:', err);
      setAutoSaveStatus('error');
    }
  }, [storageKey]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
    } catch (err) {
      console.error('Failed to clear draft:', err);
    }
  }, [storageKey]);

  // Auto-save with debounce
  const triggerAutoSave = useCallback((newResponses, sectionIndex) => {
    setAutoSaveStatus('saving');

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft({ responses: newResponses, currentSectionIndex: sectionIndex });
    }, AUTO_SAVE_DELAY);
  }, [saveDraft]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Load questionnaire and industries
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [questionnaireRes, industriesRes] = await Promise.all([
          getDefaultQuestionnaire(),
          getIndustryMultiples()
        ]);

        // Filter sections based on user type
        const filteredSections = questionnaireRes.template.sections.filter(section => {
          if (userType === 'owner') {
            return section.show_to_owner === 1;
          }
          return section.show_to_advisor === 1;
        });

        setQuestionnaire({
          ...questionnaireRes.template,
          sections: filteredSections
        });
        setIndustries(industriesRes.multiples || []);

        // Check for existing draft
        const draft = loadDraft();
        if (draft) {
          setResponses(draft.responses || {});
          setCurrentSectionIndex(draft.currentSectionIndex || 0);
          setLastSaved(draft.savedAt ? new Date(draft.savedAt) : null);
          setHasDraft(true);
        }

        setLoading(false);
      } catch (err) {
        setError('Failed to load questionnaire. Please try again.');
        setLoading(false);
      }
    };

    loadData();
  }, [userType]);

  // Calculate risk score when risk answers change
  useEffect(() => {
    if (!questionnaire) return;

    const riskSection = questionnaire.sections.find(s => s.code === 'risk_assessment');
    if (!riskSection) return;

    let score = 0;
    let answeredCount = 0;

    for (const question of riskSection.questions) {
      const value = responses[question.id];
      if (value && question.options) {
        const option = question.options.find(o => o.value === value);
        if (option) {
          score += option.score;
          answeredCount++;
        }
      }
    }

    if (answeredCount > 0) {
      setRiskScore({ score, answeredCount, total: riskSection.questions.length });
    }
  }, [responses, questionnaire]);

  const handleResponseChange = useCallback((questionId, value) => {
    setResponses(prev => {
      const newResponses = {
        ...prev,
        [questionId]: value
      };
      // Trigger auto-save
      triggerAutoSave(newResponses, currentSectionIndex);
      return newResponses;
    });

    // Clear error for this question
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  }, [errors, currentSectionIndex, triggerAutoSave]);

  // Calculate overall progress
  const calculateProgress = useCallback(() => {
    if (!questionnaire) return { answered: 0, total: 0, percent: 0 };

    let totalQuestions = 0;
    let answeredQuestions = 0;

    for (const section of questionnaire.sections) {
      for (const question of section.questions) {
        // Skip conditional questions that shouldn't be visible
        if (question.conditional_on) {
          const conditionQuestionId = getQuestionIdByCode(question.conditional_on);
          if (conditionQuestionId && responses[conditionQuestionId] !== question.conditional_value) {
            continue;
          }
        }

        // Only count required questions for progress
        if (question.is_required) {
          totalQuestions++;
          if (responses[question.id]) {
            answeredQuestions++;
          }
        }
      }
    }

    return {
      answered: answeredQuestions,
      total: totalQuestions,
      percent: totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0
    };
  }, [questionnaire, responses]);

  const progress = calculateProgress();

  const validateSection = useCallback((sectionIndex) => {
    const section = questionnaire.sections[sectionIndex];
    const newErrors = {};

    for (const question of section.questions) {
      // Check visibility based on conditional logic
      if (question.conditional_on) {
        const conditionQuestion = section.questions.find(q => q.code === question.conditional_on);
        if (conditionQuestion && responses[conditionQuestion.id] !== question.conditional_value) {
          continue; // Skip validation for hidden questions
        }
      }

      // Check required fields
      if (question.is_required && !responses[question.id]) {
        newErrors[question.id] = 'This field is required';
      }

      // Validate numeric fields
      if (responses[question.id] && question.question_type === 'currency') {
        const value = parseFloat(responses[question.id]);
        if (isNaN(value) || value < 0) {
          newErrors[question.id] = 'Please enter a valid positive number';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [questionnaire, responses]);

  const handleNext = () => {
    if (validateSection(currentSectionIndex)) {
      const newIndex = Math.min(currentSectionIndex + 1, questionnaire.sections.length - 1);
      setCurrentSectionIndex(newIndex);
      // Save progress to draft
      saveDraft({ responses, currentSectionIndex: newIndex });
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    const newIndex = Math.max(currentSectionIndex - 1, 0);
    setCurrentSectionIndex(newIndex);
    // Save progress to draft
    saveDraft({ responses, currentSectionIndex: newIndex });
    window.scrollTo(0, 0);
  };

  const handleDiscardDraft = () => {
    if (window.confirm('Are you sure you want to discard your saved progress? This cannot be undone.')) {
      clearDraft();
      setResponses({});
      setCurrentSectionIndex(0);
      setLastSaved(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateSection(currentSectionIndex)) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Build VAC input from responses
      const vacInput = buildVACInput();

      // Save questionnaire responses
      const questionResponses = Object.entries(responses).map(([questionId, value]) => ({
        questionId: parseInt(questionId),
        value
      }));

      await saveQuestionnaireResponses(valuationId, questionResponses);

      // Calculate VAC
      const result = await calculateVAC({
        ...vacInput,
        valuationId,
        userId
      });

      // Clear draft on successful submission
      clearDraft();
      setSubmitting(false);
      onComplete(result.result, valuationId);
    } catch (err) {
      setSubmitting(false);
      setError(err.response?.data?.message || 'Failed to calculate valuation. Please try again.');
    }
  };

  const buildVACInput = () => {
    const input = {
      riskAnswers: {},
      financials: {},
      multipleOptions: {},
      vacTargets: {}
    };

    if (!questionnaire) return input;

    for (const section of questionnaire.sections) {
      for (const question of section.questions) {
        const value = responses[question.id];
        if (!value) continue;

        switch (section.code) {
          case 'risk_assessment':
            input.riskAnswers[question.code] = value;
            break;

          case 'financials':
            input.financials[question.code] = parseFloat(value) || 0;
            break;

          case 'adjustments':
            if (question.code === 'ownerSalary' || question.code === 'marketSalary') {
              const ownerSalary = parseFloat(responses[getQuestionIdByCode('ownerSalary')]) || 0;
              const marketSalary = parseFloat(responses[getQuestionIdByCode('marketSalary')]) || 0;
              input.financials.ownerSalaryAdjustment = ownerSalary - marketSalary;
            }
            if (question.code === 'currentRent' || question.code === 'marketRent') {
              const currentRent = parseFloat(responses[getQuestionIdByCode('currentRent')]) || 0;
              const marketRent = parseFloat(responses[getQuestionIdByCode('marketRent')]) || 0;
              input.financials.rentAdjustment = marketRent - currentRent;
            }
            break;

          case 'multiple_config':
            if (question.code === 'applyRiskWeighting') {
              input.multipleOptions.applyRiskWeighting = value === 'yes';
            } else if (question.code === 'customMultiple') {
              input.multipleOptions.customMultiple = parseFloat(value);
            } else if (question.code === 'naicsCode') {
              input.multipleOptions.naicsCode = value;
            } else {
              input.multipleOptions[question.code] = value;
            }
            break;

          case 'vac_targets':
            if (question.code === 'targetExitValue') {
              input.targetValue = parseFloat(value);
            } else if (question.code === 'projectionYears') {
              input.projectionYears = parseInt(value);
            } else {
              input.vacTargets[question.code] = parseFloat(value);
            }
            break;

          default:
            break;
        }
      }
    }

    return input;
  };

  const getQuestionIdByCode = (code) => {
    if (!questionnaire) return null;
    for (const section of questionnaire.sections) {
      const question = section.questions.find(q => q.code === code);
      if (question) return question.id;
    }
    return null;
  };

  const renderQuestion = (question) => {
    // Check conditional visibility
    if (question.conditional_on) {
      const conditionQuestionId = getQuestionIdByCode(question.conditional_on);
      if (conditionQuestionId && responses[conditionQuestionId] !== question.conditional_value) {
        return null;
      }
    }

    const value = responses[question.id] || '';
    const hasError = errors[question.id];

    switch (question.question_type) {
      case 'select':
        return (
          <div key={question.id} className="form-group">
            <label htmlFor={`q-${question.id}`}>
              {question.question_text}
              {question.is_required === 1 && <span className="required">*</span>}
            </label>
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            <select
              id={`q-${question.id}`}
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className={hasError ? 'error' : ''}
            >
              <option value="">Select an option...</option>
              {question.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasError && <span className="error-message">{hasError}</span>}
          </div>
        );

      case 'currency':
        return (
          <div key={question.id} className="form-group">
            <label htmlFor={`q-${question.id}`}>
              {question.question_text}
              {question.is_required === 1 && <span className="required">*</span>}
            </label>
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            <div className="currency-input">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                id={`q-${question.id}`}
                value={value}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                placeholder="0"
                min="0"
                step="1000"
                className={hasError ? 'error' : ''}
              />
            </div>
            {hasError && <span className="error-message">{hasError}</span>}
          </div>
        );

      case 'percentage':
        return (
          <div key={question.id} className="form-group">
            <label htmlFor={`q-${question.id}`}>
              {question.question_text}
              {question.is_required === 1 && <span className="required">*</span>}
            </label>
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            <div className="percentage-input">
              <input
                type="number"
                id={`q-${question.id}`}
                value={value ? (parseFloat(value) * 100).toString() : ''}
                onChange={(e) => handleResponseChange(question.id, (parseFloat(e.target.value) / 100).toString())}
                placeholder={question.default_value ? (parseFloat(question.default_value) * 100).toString() : '0'}
                min="0"
                max="100"
                step="1"
                className={hasError ? 'error' : ''}
              />
              <span className="percentage-symbol">%</span>
            </div>
            {hasError && <span className="error-message">{hasError}</span>}
          </div>
        );

      case 'number':
        return (
          <div key={question.id} className="form-group">
            <label htmlFor={`q-${question.id}`}>
              {question.question_text}
              {question.is_required === 1 && <span className="required">*</span>}
            </label>
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            <input
              type="number"
              id={`q-${question.id}`}
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              placeholder={question.default_value || '0'}
              min="0"
              step="0.1"
              className={hasError ? 'error' : ''}
            />
            {hasError && <span className="error-message">{hasError}</span>}
          </div>
        );

      default:
        return (
          <div key={question.id} className="form-group">
            <label htmlFor={`q-${question.id}`}>
              {question.question_text}
              {question.is_required === 1 && <span className="required">*</span>}
            </label>
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            <input
              type="text"
              id={`q-${question.id}`}
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              className={hasError ? 'error' : ''}
            />
            {hasError && <span className="error-message">{hasError}</span>}
          </div>
        );
    }
  };

  const renderSection = () => {
    if (!questionnaire) return null;

    const section = questionnaire.sections[currentSectionIndex];
    if (!section) return null;

    // Special handling for NAICS dropdown
    const questions = section.questions.map(q => {
      if (q.code === 'naicsCode' && industries.length > 0) {
        return {
          ...q,
          question_type: 'select',
          options: industries.map(ind => ({
            value: ind.naics_code,
            label: `${ind.industry_name} (${ind.naics_code})`
          }))
        };
      }
      return q;
    });

    return (
      <div className="wizard-section">
        <h2>{section.name}</h2>
        {section.description && (
          <p className="section-description">{section.description}</p>
        )}

        {section.code === 'risk_assessment' && riskScore && (
          <div className="risk-score-preview">
            <div className="risk-score-bar">
              <div
                className="risk-score-fill"
                style={{ width: `${(riskScore.score / 70) * 100}%` }}
              ></div>
            </div>
            <p>
              Risk Score: <strong>{riskScore.score}</strong> / 70
              ({riskScore.answeredCount} of {riskScore.total} questions answered)
            </p>
          </div>
        )}

        <div className="questions-container">
          {questions.map(q => renderQuestion(q))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="vac-wizard loading">
        <div className="spinner"></div>
        <p>Loading questionnaire...</p>
      </div>
    );
  }

  if (error && !questionnaire) {
    return (
      <div className="vac-wizard error-state">
        <p className="error-message">{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const totalSections = questionnaire?.sections.length || 0;
  const isLastSection = currentSectionIndex === totalSections - 1;

  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return null;
    const now = new Date();
    const diff = Math.floor((now - lastSaved) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    return lastSaved.toLocaleTimeString();
  };

  return (
    <div className="vac-wizard">
      {/* Draft Restoration Banner */}
      {hasDraft && lastSaved && (
        <div className="draft-banner">
          <div className="draft-info">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Draft restored from {formatLastSaved()}</span>
          </div>
          <button className="btn-text" onClick={handleDiscardDraft}>
            Discard &amp; Start Over
          </button>
        </div>
      )}

      <div className="wizard-header">
        <div className="header-top">
          <h1>Value Acceleration Assessment</h1>
          <div className="auto-save-status">
            {autoSaveStatus === 'saving' && (
              <span className="status saving">Saving...</span>
            )}
            {autoSaveStatus === 'saved' && (
              <span className="status saved">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
            {autoSaveStatus === 'error' && (
              <span className="status error">Save failed</span>
            )}
          </div>
        </div>

        {/* Overall Progress */}
        <div className="overall-progress">
          <span className="progress-label">{progress.percent}% complete</span>
          <span className="progress-detail">({progress.answered} of {progress.total} required questions)</span>
        </div>

        <div className="wizard-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((currentSectionIndex + 1) / totalSections) * 100}%` }}
            ></div>
          </div>
          <div className="progress-steps">
            {questionnaire?.sections.map((section, index) => (
              <div
                key={section.id}
                className={`progress-step ${index <= currentSectionIndex ? 'active' : ''} ${index < currentSectionIndex ? 'completed' : ''}`}
                title={section.name}
              >
                <span className="step-number">{index + 1}</span>
              </div>
            ))}
          </div>
          <p className="progress-text">
            Step {currentSectionIndex + 1} of {totalSections}: {questionnaire?.sections[currentSectionIndex]?.name}
          </p>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      <div className="wizard-content">
        {renderSection()}
      </div>

      <div className="wizard-actions">
        <button
          className="btn btn-secondary"
          onClick={handlePrevious}
          disabled={currentSectionIndex === 0}
        >
          Back
        </button>

        {!isLastSection ? (
          <button className="btn btn-primary" onClick={handleNext}>
            Next
          </button>
        ) : (
          <button
            className="btn btn-primary btn-success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Calculating...' : 'Calculate Value'}
          </button>
        )}
      </div>
    </div>
  );
};

export default VACWizard;
